import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { onPageReturn } from '../utils/pageReturn';

/**
 * useSSE hook for subscribing to Server-Sent Events.
 *
 * This hook manages an EventSource connection to receive real-time updates
 * from the server for streaming transcription and TTS playback.
 *
 * @param {string} url - SSE endpoint URL (e.g., '/api/sse/nodes/123/transcription-stream')
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to connect (default: false)
 * @param {Function} options.onMessage - Callback for generic messages
 * @param {Object} options.eventHandlers - Map of event names to handlers
 * @param {number} options.reconnectDelay - Delay between reconnection attempts (default: 3000ms)
 * @param {boolean} options.reconnectOnReturn - Open a fresh connection each
 *   time the user returns to the page (default: false). Only for a stream
 *   that sends what the page already has again on connect, and whose
 *   handlers skip what they have seen.
 */
export function useSSE(url, options = {}) {
  const {
    enabled = false,
    onMessage = null,
    eventHandlers = {},
    reconnectDelay = 3000,
    reconnectOnReturn = false,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);

  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const connectRef = useRef(null);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!url || !enabled) {
      return;
    }

    // Close existing connection
    disconnect();

    try {
      // Create EventSource with credentials for auth
      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        setError(null);
      };

      eventSource.onerror = (e) => {
        console.error('SSE connection error:', e);
        setIsConnected(false);

        // EventSource will automatically try to reconnect for transient errors
        // But if the connection is closed, we might need to handle it
        if (eventSource.readyState === EventSource.CLOSED) {
          setError('Connection closed');

          // Attempt to reconnect after delay
          // Use connectRef.current to get the latest connect function with current URL
          reconnectTimeoutRef.current = setTimeout(() => {
            if (connectRef.current) {
              connectRef.current();
            }
          }, reconnectDelay);
        }
      };

      // Handle generic message events (data-only, no event type)
      eventSource.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          setLastEvent({ type: 'message', data });
          if (onMessage) {
            onMessage(data);
          }
        } catch (err) {
          console.error('Failed to parse SSE message:', err);
        }
      };

      // Register handlers for specific event types
      Object.entries(eventHandlers).forEach(([eventType, handler]) => {
        eventSource.addEventListener(eventType, (e) => {
          try {
            // Skip if data is undefined or empty (can happen on connection errors)
            if (!e.data) {
              console.warn(`SSE event ${eventType} received with no data`);
              return;
            }
            const data = JSON.parse(e.data);
            setLastEvent({ type: eventType, data });
            handler(data);
          } catch (err) {
            console.error(`Failed to parse SSE event ${eventType}:`, err);
          }
        });
      });

    } catch (err) {
      console.error('Failed to create EventSource:', err);
      setError(err.message);
    }
  }, [url, enabled, onMessage, eventHandlers, reconnectDelay, disconnect]);

  // Keep connectRef updated so reconnection timer always uses latest function
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // Connect/disconnect based on enabled state
  useEffect(() => {
    if (enabled && url) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, url, connect, disconnect]);

  // The user is back on the page (#374): replace the connection. iOS can
  // leave a suspended page's EventSource dead without an error event, so
  // neither the browser's own retry nor the onerror reconnect above runs,
  // and the page would wait on a stream that never speaks again. A live
  // connection replaced this way costs one reconnect.
  useEffect(() => {
    if (!reconnectOnReturn || !enabled || !url) return undefined;
    return onPageReturn(() => {
      if (connectRef.current) connectRef.current();
    });
  }, [reconnectOnReturn, enabled, url]);

  return {
    isConnected,
    error,
    lastEvent,
    connect,
    disconnect,
  };
}

/**
 * useTranscriptionSSE - Specialized hook for streaming transcription updates.
 *
 * @param {number} nodeId - Node ID to subscribe to
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to connect
 * @param {Function} options.onChunkComplete - Called when a chunk is transcribed
 * @param {Function} options.onAllComplete - Called when all chunks are done
 * @param {Function} options.onError - Called on transcription error
 */
export function useTranscriptionSSE(nodeId, options = {}) {
  const {
    enabled = false,
    onChunkComplete = null,
    onAllComplete = null,
    onError = null,
  } = options;

  const [chunks, setChunks] = useState([]); // Array of { index, text }
  const [isComplete, setIsComplete] = useState(false);
  const [finalContent, setFinalContent] = useState(null);

  // Use REACT_APP_BACKEND_URL for SSE since EventSource doesn't go through CRA proxy
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const url = nodeId ? `${backendUrl}/api/sse/nodes/${nodeId}/transcription-stream` : null;

  const eventHandlers = {
    chunk_complete: (data) => {
      setChunks(prev => {
        // Add or update chunk
        const existing = prev.find(c => c.index === data.chunk_index);
        if (existing) {
          return prev.map(c =>
            c.index === data.chunk_index ? { ...c, text: data.text } : c
          );
        }
        return [...prev, { index: data.chunk_index, text: data.text }].sort(
          (a, b) => a.index - b.index
        );
      });
      if (onChunkComplete) {
        onChunkComplete(data);
      }
    },
    chunk_error: (data) => {
      if (onError) {
        onError(data);
      }
    },
    all_complete: (data) => {
      setIsComplete(true);
      setFinalContent(data.content);
      if (onAllComplete) {
        onAllComplete(data);
      }
    },
    error: (data) => {
      if (onError) {
        onError(data);
      }
    },
    heartbeat: () => {
      // Keep-alive, no action needed
    },
  };

  const { isConnected, error, disconnect } = useSSE(url, {
    enabled,
    eventHandlers,
  });

  // Get assembled transcript from chunks
  const getAssembledTranscript = useCallback(() => {
    return chunks.map(c => c.text).join('\n\n');
  }, [chunks]);

  // Reset state
  const reset = useCallback(() => {
    setChunks([]);
    setIsComplete(false);
    setFinalContent(null);
  }, []);

  return {
    isConnected,
    error,
    chunks,
    isComplete,
    finalContent,
    getAssembledTranscript,
    disconnect,
    reset,
  };
}

/**
 * useDraftTranscriptionSSE - Specialized hook for draft-based streaming transcription.
 *
 * This is similar to useTranscriptionSSE but works with session_id instead of node_id,
 * and includes content_update events as the draft is updated in real-time.
 *
 * @param {string} sessionId - Session ID to subscribe to
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to connect
 * @param {Function} options.onChunkComplete - Called when a chunk is transcribed
 * @param {Function} options.onContentUpdate - Called when draft content is updated
 * @param {Function} options.onAllComplete - Called when all chunks are done
 * @param {Function} options.onError - Called on transcription error
 */
export function useDraftTranscriptionSSE(sessionId, options = {}) {
  const {
    enabled = false,
    onChunkComplete = null,
    onContentUpdate = null,
    onAllComplete = null,
    onError = null,
  } = options;

  const [chunks, setChunks] = useState([]); // Array of { index, text }
  const [isComplete, setIsComplete] = useState(false);
  const [finalContent, setFinalContent] = useState(null);
  const [draftContent, setDraftContent] = useState('');

  // Track last received chunk for reconnection
  // Use ref for tracking (no re-renders) and state for URL (triggers reconnect when needed)
  const lastChunkIndexRef = useRef(-1);
  const [reconnectChunkIndex, setReconnectChunkIndex] = useState(-1);

  // Store callback refs to ensure handlers always call latest callbacks
  const onChunkCompleteRef = useRef(onChunkComplete);
  const onContentUpdateRef = useRef(onContentUpdate);
  const onAllCompleteRef = useRef(onAllComplete);
  const onErrorRef = useRef(onError);

  // Track last event time for stale connection detection
  const lastEventTimeRef = useRef(Date.now());

  // Keep refs updated
  useEffect(() => {
    onChunkCompleteRef.current = onChunkComplete;
    onContentUpdateRef.current = onContentUpdate;
    onAllCompleteRef.current = onAllComplete;
    onErrorRef.current = onError;
  }, [onChunkComplete, onContentUpdate, onAllComplete, onError]);

  // Use REACT_APP_BACKEND_URL for SSE since EventSource doesn't go through CRA proxy
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Include last_chunk param for reconnection support - only add if we've received chunks
  const lastChunkParam = reconnectChunkIndex >= 0 ? `?last_chunk=${reconnectChunkIndex}` : '';
  const url = sessionId ? `${backendUrl}/api/sse/drafts/${sessionId}/transcription-stream${lastChunkParam}` : null;

  // Memoize eventHandlers to prevent unnecessary reconnections
  // Handlers use refs internally to always call latest callbacks
  const eventHandlers = useMemo(() => ({
    chunk_complete: (data) => {
      // Track last event time for stale connection detection
      lastEventTimeRef.current = Date.now();
      // Track last received chunk for reconnection (ref doesn't trigger re-renders)
      lastChunkIndexRef.current = Math.max(lastChunkIndexRef.current, data.chunk_index);

      setChunks(prev => {
        // Add or update chunk
        const existing = prev.find(c => c.index === data.chunk_index);
        if (existing) {
          return prev.map(c =>
            c.index === data.chunk_index ? { ...c, text: data.text } : c
          );
        }
        return [...prev, { index: data.chunk_index, text: data.text }].sort(
          (a, b) => a.index - b.index
        );
      });
      if (onChunkCompleteRef.current) {
        onChunkCompleteRef.current(data);
      }
    },
    chunk_error: (data) => {
      lastEventTimeRef.current = Date.now();
      if (onErrorRef.current) {
        onErrorRef.current(data);
      }
    },
    content_update: (data) => {
      lastEventTimeRef.current = Date.now();
      setDraftContent(data.content);
      if (onContentUpdateRef.current) {
        onContentUpdateRef.current(data);
      }
    },
    all_complete: (data) => {
      lastEventTimeRef.current = Date.now();
      setIsComplete(true);
      setFinalContent(data.content);
      setDraftContent(data.content);
      if (onAllCompleteRef.current) {
        onAllCompleteRef.current(data);
      }
    },
    error: (data) => {
      lastEventTimeRef.current = Date.now();
      if (onErrorRef.current) {
        onErrorRef.current(data);
      }
    },
    heartbeat: () => {
      // Track last event time for stale connection detection
      lastEventTimeRef.current = Date.now();
    },
  }), []); // Empty deps - handlers use refs internally

  const { isConnected, error, disconnect } = useSSE(url, {
    enabled,
    eventHandlers,
  });

  // When connection drops, update reconnectChunkIndex to trigger reconnect with last_chunk param
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (wasConnectedRef.current && !isConnected && enabled && !isComplete) {
      // Connection was lost - update state to trigger reconnect with proper URL
      setReconnectChunkIndex(lastChunkIndexRef.current);
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected, enabled, isComplete]);

  // Stale connection detection: if no events received for 45s (3x heartbeat interval),
  // force a reconnect by updating reconnectChunkIndex
  useEffect(() => {
    if (!enabled || isComplete) return;

    const STALE_THRESHOLD_MS = 45000; // 45 seconds (3x the 15s heartbeat interval)
    const CHECK_INTERVAL_MS = 10000; // Check every 10 seconds

    const intervalId = setInterval(() => {
      const timeSinceLastEvent = Date.now() - lastEventTimeRef.current;
      if (timeSinceLastEvent > STALE_THRESHOLD_MS && isConnected) {
        console.warn(`SSE connection appears stale (no events for ${Math.round(timeSinceLastEvent / 1000)}s), forcing reconnect`);
        // Force disconnect and reconnect by updating reconnectChunkIndex
        disconnect();
        setReconnectChunkIndex(lastChunkIndexRef.current);
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [enabled, isComplete, isConnected, disconnect]);

  // Get assembled transcript from chunks
  const getAssembledTranscript = useCallback(() => {
    return chunks.map(c => c.text).join('\n\n');
  }, [chunks]);

  // Reset state
  const reset = useCallback(() => {
    setChunks([]);
    setIsComplete(false);
    setFinalContent(null);
    setDraftContent('');
    lastChunkIndexRef.current = -1;
    setReconnectChunkIndex(-1);
  }, []);

  return {
    isConnected,
    error,
    chunks,
    isComplete,
    finalContent,
    draftContent,
    getAssembledTranscript,
    disconnect,
    reset,
  };
}

/**
 * useTTSStreamSSE - Specialized hook for streaming TTS playback updates.
 *
 * @param {number} entityId - Node or Profile ID to subscribe to
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to connect
 * @param {string} options.entityType - 'node' (default), 'profile' or 'item' (saved reference)
 * @param {Function} options.onChunkReady - Called when an audio chunk is ready
 * @param {Function} options.onAllComplete - Called when all chunks are done
 * @param {Function} options.onError - Called when the server reports that
 *   generation failed (its `error` event). The server has closed the
 *   stream and refuses a new one, so the caller turns `enabled` off.
 * @param {boolean} options.reconnectOnReturn - Reconnect when the user
 *   returns to the page (see useSSE). The stream sends every chunk again
 *   on connect; onChunkReady still gets each chunk index once.
 */
export function useTTSStreamSSE(entityId, options = {}) {
  const {
    enabled = false,
    entityType = 'node',
    onChunkReady = null,
    onAllComplete = null,
    onError = null,
    reconnectOnReturn = false,
  } = options;

  const [audioChunks, setAudioChunks] = useState([]); // Array of { index, url, duration }
  const [isComplete, setIsComplete] = useState(false);
  const [finalUrl, setFinalUrl] = useState(null);

  // Store callback refs to ensure handlers always call latest callbacks
  const onChunkReadyRef = useRef(onChunkReady);
  const onAllCompleteRef = useRef(onAllComplete);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onChunkReadyRef.current = onChunkReady;
    onAllCompleteRef.current = onAllComplete;
    onErrorRef.current = onError;
  }, [onChunkReady, onAllComplete, onError]);

  // Use REACT_APP_BACKEND_URL for SSE since EventSource doesn't go through CRA proxy
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const ssePathSegment = entityType === 'profile'
    ? `profiles/${entityId}`
    : entityType === 'item'
      ? `items/${entityId}`
      : `nodes/${entityId}`;
  const url = entityId ? `${backendUrl}/api/sse/${ssePathSegment}/tts-stream` : null;

  // Debug: log SSE URL changes
  const prevUrlRef = useRef(null);
  if (url !== prevUrlRef.current) {
    console.log('[TTS-SSE] URL changed:', { entityId, url, enabled, prevUrl: prevUrlRef.current });
    prevUrlRef.current = url;
  }

  // Chunk indices already delivered to onChunkReady. The TTS stream has no
  // last_chunk reconnect param, so a dropped-and-reconnected EventSource
  // replays every chunk from the start — without this guard each replayed
  // chunk would be appended to the audio queue a second time.
  const receivedIndicesRef = useRef(new Set());
  useEffect(() => {
    receivedIndicesRef.current = new Set();
  }, [url]);

  // Memoize eventHandlers to prevent unnecessary reconnections
  // Handlers use refs internally to always call latest callbacks
  const eventHandlers = useMemo(() => ({
    chunk_ready: (data) => {
      if (receivedIndicesRef.current.has(data.chunk_index)) return;
      receivedIndicesRef.current.add(data.chunk_index);
      setAudioChunks(prev => {
        const existing = prev.find(c => c.index === data.chunk_index);
        if (existing) {
          return prev;
        }
        return [...prev, {
          index: data.chunk_index,
          url: data.audio_url,
          duration: data.duration != null ? data.duration : null
        }].sort(
          (a, b) => a.index - b.index
        );
      });
      if (onChunkReadyRef.current) {
        onChunkReadyRef.current(data);
      }
    },
    all_complete: (data) => {
      setIsComplete(true);
      setFinalUrl(data.tts_url);
      if (onAllCompleteRef.current) {
        onAllCompleteRef.current(data);
      }
    },
    // The server's `error` event: generation failed, or the entry is gone.
    // A dropped connection fires `error` too, without data; useSSE skips
    // those.
    error: (data) => {
      if (onErrorRef.current) {
        onErrorRef.current(data);
      }
    },
    heartbeat: () => {
      // Keep-alive, no action needed
    },
  }), []); // Empty deps - handlers use refs internally

  const { isConnected, error, connect, disconnect } = useSSE(url, {
    enabled,
    eventHandlers,
    reconnectOnReturn,
  });

  // Get ordered list of audio URLs for queue playback
  const getAudioQueue = useCallback(() => {
    return audioChunks.map(c => c.url);
  }, [audioChunks]);

  // Reset state
  const reset = useCallback(() => {
    setAudioChunks([]);
    setIsComplete(false);
    setFinalUrl(null);
    receivedIndicesRef.current = new Set();
  }, []);

  return {
    isConnected,
    error,
    audioChunks,
    isComplete,
    finalUrl,
    getAudioQueue,
    // Force a fresh EventSource. iOS Safari kills SSE connections on app
    // suspension WITHOUT firing onerror, so the base hook's auto-reconnect
    // never engages — callers detecting a stalled stream (#242 recovery)
    // use this. The TTS stream replays every chunk from the start on
    // reconnect; consumers dedup via chunk_index / queue-URL guards.
    reconnect: connect,
    disconnect,
    reset,
  };
}

/**
 * useLlmTextStream - an LLM reply's text while it is generated (#367).
 *
 * Subscribes to /api/sse/nodes/<id>/llm-stream: a snapshot (the whole
 * text so far) on connect and whenever the model call restarted, deltas
 * as text is appended, and done once the node stops generating (the final
 * content then comes from the node / llm-status, not from here).
 *
 * @param {number|null} nodeId - the pending LLM node
 * @param {Object} options
 * @param {boolean} options.enabled - whether to connect
 * @param {string} options.initialText - text already known (the node
 *   fetch carries streaming_content), shown until the first event
 * @returns {{ text: string, done: object|null }}
 */
export function useLlmTextStream(nodeId, { enabled = false, initialText = '' } = {}) {
  const [text, setText] = useState(initialText || '');
  const [done, setDone] = useState(null);

  // A different node starts from its own text.
  useEffect(() => {
    setText(initialText || '');
    setDone(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const url = nodeId ? `${backendUrl}/api/sse/nodes/${nodeId}/llm-stream` : null;

  const eventHandlers = useMemo(() => ({
    snapshot: (data) => setText(data.text || ''),
    delta: (data) => setText(prev => prev + (data.text || '')),
    done: (data) => setDone(data || {}),
    heartbeat: () => {},
  }), []);

  // Stop listening once the node is done: the server closes the stream,
  // and EventSource would otherwise reconnect just to hear "done" again.
  // A return to the page reconnects (#374): the stream opens with a
  // snapshot of the whole text, so a connection that died while the page
  // was away loses nothing.
  useSSE(url, { enabled: enabled && !done, eventHandlers, reconnectOnReturn: true });

  return { text, done };
}
